import React, { useState, useEffect, useRef } from 'react';
import './styles.css';

/**
 * 搜索框組件
 * 用於顯示帶有防抖功能的搜索輸入框
 * 
 * @param {Object} props - 組件屬性
 * @param {string} props.value - 搜索值
 * @param {Function} props.onChange - 值變更回調函數
 * @param {string} props.placeholder - 佔位符文本
 * @param {number} props.debounceTime - 防抖時間（毫秒）
 * @param {string} props.className - 額外的CSS類名
 * @returns {JSX.Element} 搜索框組件
 */
function SearchInput({
    value: externalValue,
    onChange,
    placeholder = '搜索...',
    debounceTime = 300,
    className = '',
    ...rest
}) {
    // 內部狀態，用於即時顯示用戶輸入
    const [internalValue, setInternalValue] = useState(externalValue || '');
    const timerRef = useRef(null);
    const lastCallTimeRef = useRef(0);

    // 當外部值變化時，更新內部狀態
    useEffect(() => {
        setInternalValue(externalValue || '');
    }, [externalValue]);

    // 處理輸入變化
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInternalValue(newValue);

        const now = Date.now();

        // 清除之前的定時器
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        // 如果距離上次調用時間太短，立即更新
        if (now - lastCallTimeRef.current < 100) {
            onChange(newValue);
            lastCallTimeRef.current = now;
            return;
        }

        // 設置新的定時器
        timerRef.current = setTimeout(() => {
            onChange(newValue);
            lastCallTimeRef.current = Date.now();
        }, debounceTime);
    };

    // 組件卸載時清理定時器
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    // 處理清除按鈕點擊
    const handleClear = () => {
        setInternalValue('');
        onChange('');
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    };

    return (
        <div className={`search-input-container ${className}`}>
            <div className="search-input-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
            </div>

            <input
                type="text"
                value={internalValue}
                onChange={handleInputChange}
                placeholder={placeholder}
                className="search-input"
                {...rest}
            />

            {internalValue && (
                <button
                    type="button"
                    className="search-input-clear"
                    onClick={handleClear}
                    aria-label="清除搜索"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                </button>
            )}
        </div>
    );
}

export default SearchInput; 